<script setup lang="ts">
import { ChevronDown } from 'lucide-vue-next'
import { useInfiniteScroll } from '@vueuse/core'
import dayjs from 'dayjs'
import CardTitle from '@/components/CardTitle.vue'
import { webappPointTypes } from './activityFeed/pointActivityTypes'

import type { Icons } from '@/components/base/BaseIcon.vue'
import type { FeedActivity, HistoryReconstructionStatus } from '@/types/customer/ActivitiesFeed'
import ActivityCardRouter from '@/views/customer/Components/activityFeed/ActivityCardRouter.vue'

const { t } = useI18n()

const props = withDefaults(
  defineProps<{
    activities: FeedActivity[]
    height: number
    isLoaded: boolean
    reconstructionStatus?: HistoryReconstructionStatus | null
    hasMore?: boolean
    isLoadingMore?: boolean
  }>(),
  {
    reconstructionStatus: null,
    hasMore: false,
    isLoadingMore: false
  }
)

const emit = defineEmits<{
  (e: 'loadMore'): void
  (e: 'filterChange', feedTypes: string[] | null): void
}>()

const scrollContainerRef = ref<HTMLElement | null>(null)

useInfiniteScroll(
  scrollContainerRef,
  () => {
    if (props.hasMore && !props.isLoadingMore) {
      emit('loadMore')
    }
  },
  {
    distance: 100,
    canLoadMore: () => props.hasMore === true && props.isLoadingMore === false
  }
)

const feedTypeFilters: {
  feedTypes: string[] | null
  labelKey: string
  icon: Icons | null
  color: string
}[] = [
  {
    feedTypes: null,
    labelKey: 'all',
    icon: null,
    color: 'text-yellow-400 group-hover:text-yellow-500'
  },
  {
    feedTypes: ['checkin'],
    labelKey: 'checkin',
    icon: 'HeroMapPinIconSolid',
    color: 'text-yellow-400 group-hover:text-yellow-500'
  },
  {
    feedTypes: ['reference_store.changed'],
    labelKey: 'referenceStoreChanged',
    icon: 'IconoirShop',
    color: 'text-teal-500 group-hover:text-teal-600'
  },
  {
    feedTypes: ['enrichment.answer'],
    labelKey: 'answerCrm',
    icon: 'HeroIdentificationIconSolid',
    color: ' text-muted-foreground group-hover:text-muted-foreground'
  },
  {
    feedTypes: ['reward.claimed'],
    labelKey: 'reward',
    icon: 'HeroGiftIconSolid',
    color: 'text-primary/50 group-hover:text-primary'
  },
  {
    feedTypes: ['survey.answer'],
    labelKey: 'survey',
    icon: 'HeroChartPieIconSolid',
    color: 'text-indigo-400 group-hover:text-indigo-500'
  },
  {
    feedTypes: [
      'campaign.message.sent',
      'autopilot.message.sent',
      'marketing.flow.message.sent',
      'marketing.flow.push.sent',
      'order.notification.sent',
      'reward.message.sent',
      'contest.game.notification.sent',
      'missed.call.notification.sent',
      'customer.notification.sent'
    ],
    labelKey: 'message',
    icon: 'HeroChatBubbleOvalLeftEllipsisIconSolid',
    color: 'text-green-400 group-hover:text-green-500'
  },
  {
    feedTypes: ['contest.participation'],
    labelKey: 'contestGameParticipation',
    icon: 'HeroPlayIconSolid',
    color: 'text-red-400 group-hover:text-red-500'
  },
  {
    feedTypes: ['points.manual'],
    labelKey: 'manualPoint',
    icon: 'HeroCursorArrowRaysIconSolid',
    color: 'text-pink-600 group-hover:text-pink-900'
  },
  {
    feedTypes: ['missed_call'],
    labelKey: 'missedCalls',
    icon: 'HeroPhoneXMarkIconSolid',
    color: 'text-indigo-400 group-hover:text-indigo-500'
  },
  {
    feedTypes: ['autopilot.message.converted'],
    labelKey: 'autopilot',
    icon: 'HeroBoltIconSolid',
    color: 'text-yellow-400 group-hover:text-yellow-500'
  },
  {
    feedTypes: ['customer_point', ...webappPointTypes],
    labelKey: 'webapp',
    icon: 'HeroCircleStackIconSolid',
    color: ' text-amber-500 group-hover:text-amber-600'
  },
  {
    feedTypes: ['segment.join', 'segment.leave'],
    labelKey: 'segment',
    icon: 'HeroSquare3Stack3DIconSolid',
    color: 'text-blue-500 group-hover:text-blue-600'
  },
  {
    feedTypes: ['email.optin', 'email.optout', 'email.optunknown'],
    labelKey: 'emailSubscription',
    icon: 'HeroEnvelopeIconSolid',
    color: 'text-cyan-500 group-hover:text-cyan-600'
  },
  {
    feedTypes: ['sms.optin', 'sms.optout', 'sms.optunknown'],
    labelKey: 'smsSubscription',
    icon: 'HeroPhoneIconSolid',
    color: 'text-orange-500 group-hover:text-orange-600'
  }
]

const selectedFeedTypes = ref<string[] | null>(null)

const filteredActivities = computed(() => {
  let filtered = [...props.activities]

  if (selectedFeedTypes.value && selectedFeedTypes.value.length > 0) {
    const feedTypes = selectedFeedTypes.value
    filtered = filtered.filter((activity) => feedTypes.includes(activity.type))
  }

  return filtered.sort((a, b) => {
    return dayjs(b.occurred_at).valueOf() - dayjs(a.occurred_at).valueOf()
  })
})

const handleFilterChange = (feedTypes: string[] | null) => {
  selectedFeedTypes.value = feedTypes
  emit('filterChange', feedTypes)
}

const getFilterLabel = (labelKey: string): string => {
  return t(`pages.customer.customerDetailPage.filters.${labelKey}`)
}

const getSelectedFilterLabel = computed(() => {
  if (!selectedFeedTypes.value || selectedFeedTypes.value.length === 0) {
    return t('pages.customer.customerDetailPage.filters.all')
  }
  const feedTypes = selectedFeedTypes.value
  const filter = feedTypeFilters.find((f) => {
    if (!f.feedTypes) return false
    return f.feedTypes.length === feedTypes.length && f.feedTypes.every((type) => feedTypes.includes(type))
  })
  return filter ? getFilterLabel(filter.labelKey) : t('pages.customer.customerDetailPage.filters.all')
})

const getSelectedFilterLabelKey = computed(() => {
  if (!selectedFeedTypes.value || selectedFeedTypes.value.length === 0) {
    return null
  }
  const feedTypes = selectedFeedTypes.value
  const filter = feedTypeFilters.find((f) => {
    if (!f.feedTypes) return false
    return f.feedTypes.length === feedTypes.length && f.feedTypes.every((type) => feedTypes.includes(type))
  })
  return filter ? filter.labelKey : null
})
</script>

<template>
  <section
    aria-labelledby="timeline-title"
    class="mb-36 w-full overflow-hidden rounded-lg border bg-white sm:mb-0 lg:w-5/12"
  >
    <div class="h-full flex-1">
      <div class="border-b">
        <div class="mx-4 flex items-center justify-between py-5 sm:mx-6">
          <CardTitle
            :title="t('pages.customer.customerDetailPage.blocks.activity.title')"
            :description="t('pages.customer.customerDetailPage.blocks.activity.description')"
          />
          <DropdownMenu>
            <DropdownMenuTrigger as-child>
              <Button variant="outline">
                <span>{{ getSelectedFilterLabel }}</span>
                <ChevronDown />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" class="w-56">
              <DropdownMenuItem
                v-for="filter in feedTypeFilters"
                :key="filter.labelKey"
                class="group flex cursor-pointer items-center px-4 py-2 text-sm text-foreground focus:bg-accent focus:text-foreground"
                @click="handleFilterChange(filter.feedTypes)"
              >
                <BaseIcon v-if="filter.icon" :name="filter.icon" class="mr-3 h-5 w-5" :class="filter.color" />
                {{ getFilterLabel(filter.labelKey) }}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
      <div
        ref="scrollContainerRef"
        class="h-full overflow-y-auto px-4 pt-5 sm:px-6"
        :style="`max-height: ${height - 110}px;`"
      >
        <div v-if="!isLoaded" class="flex items-center justify-center py-4">
          <div class="flex items-center justify-center p-4">
            <div class="h-8 w-8 animate-spin rounded-full border-2 border-solid border-b-primary"></div>
          </div>
        </div>
        <div v-else-if="filteredActivities.length > 0" class="pr-2">
          <ul>
            <li v-for="(activity, index) in filteredActivities" :key="activity.id">
              <ActivityCardRouter :activity="activity" :show-connector="index < filteredActivities.length - 1" />
            </li>
          </ul>
          <div v-if="isLoadingMore" class="flex items-center justify-center py-4">
            <div class="flex items-center justify-center p-4">
              <div class="h-6 w-6 animate-spin rounded-full border-2 border-solid border-b-primary"></div>
            </div>
          </div>
        </div>
        <div v-else class="mt-4 text-sm text-muted-foreground" :style="`max-height: ${height - 168}px;`">
          {{
            getSelectedFilterLabelKey
              ? t(`pages.customer.customerDetailPage.activities.empty.${getSelectedFilterLabelKey}`)
              : t('pages.customer.customerDetailPage.activities.empty.default')
          }}
        </div>
      </div>
    </div>
  </section>
</template>
